import React from 'react';
import SectionHeader from './SectionHeader';
import NotesInput from './NotesInput';
import CurrencyInput from './CurrencyInput';

const DEBT_TYPES = [
  'Mortgage',
  '2nd Mortgage',
  'HELOC',
  'Auto Loan',
  'Student Loan',
  'Credit Card',
  'Personal Line of Credit',
  'Other',
];

const EMPTY_ENTRY = {
  type: '',
  institution: '',
  cc_paid_monthly: '',
  principal_balance: null,
  monthly_payment: null,
  interest_rate: null,
  remaining_term: '',
  is_new: '',
};

/**
 * Section 7 — Debts &amp; Loans.
 *
 * Captures loans the household holds — type, institution, balance,
 * payment, rate, and remaining term. Even zero-balance or zero-interest
 * accounts are useful, so all fields are optional.
 */
export default function StepDebts({ data, onChange }) {
  const value = data || {};
  const baseEntries = (value.entries && value.entries.length > 0)
    ? value.entries
    : [{ ...EMPTY_ENTRY }];

  const lastHasData = Object.entries(baseEntries[baseEntries.length - 1] || {})
    .some(([, v]) => v !== null && v !== undefined && v !== '');

  const entries = lastHasData && baseEntries.length < 12
    ? [...baseEntries, { ...EMPTY_ENTRY }]
    : baseEntries;

  function updateEntry(idx, field, val) {
    const next = entries.map((e, i) => (i === idx ? { ...e, [field]: val } : e));
    onChange({ ...value, entries: next });
  }

  function removeEntry(idx) {
    const next = entries.filter((_, i) => i !== idx);
    onChange({ ...value, entries: next.length > 0 ? next : [{ ...EMPTY_ENTRY }] });
  }

  return (
    <div>
      <SectionHeader
        number={7}
        title="Debts &amp; Loans"
        tip="List any debts or loans — common types include mortgages,
          home equity lines, auto loans, student loans, credit cards, or
          personal lines of credit. Even if you pay off balances each
          month, please list them. We don't want the monthly balance due
          if it's paid off (use $0) — but we still want each line item."
        showAnnualNote={false}
      />

      {entries.map((entry, idx) => {
        const isCC = entry.type === 'Credit Card';
        const ccPaidMonthly = isCC && entry.cc_paid_monthly === 'yes';
        return (
        <div key={idx} className="cashflow-section-card">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="cashflow-subsection-title mb-0">
              Debt #{idx + 1}
            </h6>
            {entries.length > 1 && (
              <button
                type="button"
                className="btn btn-link btn-sm text-muted p-0"
                onClick={() => removeEntry(idx)}
                title="Remove this debt"
              >
                <i className="bi bi-x-lg" />
              </button>
            )}
          </div>
          <div className="row g-2">
            <div className="col-md-4">
              <label className="form-label">Type</label>
              <select
                className="form-select"
                value={entry.type || ''}
                onChange={(e) => updateEntry(idx, 'type', e.target.value)}
              >
                <option value="">Select…</option>
                {DEBT_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Institution</label>
              <input
                type="text"
                className="form-control"
                value={entry.institution || ''}
                onChange={(e) => updateEntry(idx, 'institution', e.target.value)}
              />
            </div>
            {isCC && (
              <div className="col-md-4">
                <label className="form-label">Do you pay this off monthly?</label>
                <select
                  className="form-select"
                  value={entry.cc_paid_monthly || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    const patch = { cc_paid_monthly: val };
                    if (val === 'yes') {
                      patch.principal_balance = 0;
                      patch.monthly_payment = 0;
                      patch.remaining_term = '';
                    }
                    const next = entries.map((en, i) =>
                      i === idx ? { ...en, ...patch } : en
                    );
                    onChange({ ...value, entries: next });
                  }}
                >
                  <option value="">Select…</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            )}
            {!ccPaidMonthly && (
              <div className="col-md-4">
                <CurrencyInput
                  label="Principal balance"
                  id={`debt_principal_${idx}`}
                  value={entry.principal_balance}
                  onChange={(v) => updateEntry(idx, 'principal_balance', v)}
                  noToggle
                />
              </div>
            )}
            {!ccPaidMonthly && (
              <div className="col-md-3">
                <CurrencyInput
                  label="Monthly payment"
                  id={`debt_payment_${idx}`}
                  value={entry.monthly_payment}
                  onChange={(v) => updateEntry(idx, 'monthly_payment', v)}
                  noToggle
                />
              </div>
            )}
            <div className="col-md-3">
              <label className="form-label">Interest rate (%)</label>
              <input
                type="number"
                step="0.01"
                className="form-control"
                value={entry.interest_rate ?? ''}
                onChange={(e) =>
                  updateEntry(
                    idx,
                    'interest_rate',
                    e.target.value === '' ? null : parseFloat(e.target.value),
                  )
                }
              />
            </div>
            {!ccPaidMonthly && (
              <div className="col-md-3">
                <label className="form-label">Remaining term</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. 27 yrs or 36 mos"
                  value={entry.remaining_term || ''}
                  onChange={(e) => updateEntry(idx, 'remaining_term', e.target.value)}
                />
              </div>
            )}
            <div className="col-md-3">
              <label className="form-label">New since last met?</label>
              <select
                className="form-select"
                value={entry.is_new || ''}
                onChange={(e) => updateEntry(idx, 'is_new', e.target.value)}
              >
                <option value="">Select…</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
                <option value="unsure">I'm not sure</option>
              </select>
            </div>
          </div>
        </div>
        );
      })}

      <NotesInput
        value={value.notes}
        onChange={(v) => onChange({ ...value, notes: v })}
      />
    </div>
  );
}
